import { API_BASE } from "@/lib/sovereign";
import { getAccessToken, getAuthenticatedWallet } from "@/hooks/use-auth";

export interface RequestOptions extends RequestInit {
  tenantId?: string;
  walletAddress?: string;
  chainId?: number;
}

function tenantIdFromHost(): string {
  if (typeof window === "undefined") return "default";
  const meta = document.querySelector<HTMLMetaElement>('meta[name="tenant-id"]');
  if (meta?.content) return meta.content;
  const host = window.location.host;
  const first = host.split(".")[0];
  return first && first !== "localhost" ? first : "default";
}

/**
 * Read chain id from an injected provider (MetaMask etc.) when present.
 * We intentionally do NOT read the wallet address from `window.ethereum` —
 * Privy doesn't reliably populate `selectedAddress` (embedded wallets, some
 * injected providers go through Privy's proxy). The wallet comes from
 * `getAuthenticatedWallet()` instead, which `AuthProvider` keeps in sync
 * with the authenticated Privy user.
 */
function chainIdFromBrowser(): number | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    const eth = (window as unknown as { ethereum?: { chainId?: string } }).ethereum;
    return eth?.chainId ? parseInt(eth.chainId, 16) : undefined;
  } catch {
    return undefined;
  }
}

export async function apiFetch<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const tenantId = opts.tenantId ?? tenantIdFromHost();
  const token = await getAccessToken();
  const wallet = (opts.walletAddress ?? getAuthenticatedWallet() ?? undefined)?.toLowerCase();
  const chainId = opts.chainId ?? chainIdFromBrowser();

  const res = await fetch(`${API_BASE}${path}`, {
    ...opts,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      "X-Tenant-ID": tenantId,
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(wallet ? { "X-Wallet-Address": wallet } : {}),
      ...(chainId ? { "X-Chain-Id": String(chainId) } : {}),
      ...(opts.headers ?? {}),
    },
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`${res.status} ${res.statusText}: ${text || path}`);
  }
  return (await res.json()) as T;
}
